/**
 * Gmail attention-rule contracts. A user's "what counts as attention in
 * Gmail" configuration is stored non-secret at `user_settings/{uid}.gmailRules`
 * and compiled into a Gmail search query by `buildGmailSearchQuery`, which
 * both the SPA (live preview) and the BFF (the actual list fetch) use so the
 * preview never lies about what will be fetched.
 */
const MIN_LOOKBACK_DAYS = 1;
const MAX_LOOKBACK_DAYS = 90;
const MIN_MAX_THREADS = 5;
const MAX_MAX_THREADS = 50;
const MAX_LIST_ENTRIES = 20;
const MAX_LIST_ENTRY_LENGTH = 120;
const MAX_CUSTOM_QUERY_LENGTH = 500;
export function defaultGmailAttentionRules() {
    return {
        onlyUnread: true,
        onlyDirectToMe: true,
        includeStarred: true,
        includeImportant: false,
        excludeCategories: ['promotions', 'social', 'updates', 'forums'],
        excludeNoReply: true,
        labels: [],
        senderAllowlist: [],
        senderBlocklist: [],
        lookbackDays: 14,
        maxThreads: 25,
        customQuery: '',
    };
}
export function defaultGmailConnectionSettings() {
    return { connected: false, email: '', rules: defaultGmailAttentionRules() };
}
export function recentGmailFeedRules() {
    return {
        ...defaultGmailAttentionRules(),
        onlyUnread: false,
        onlyDirectToMe: false,
        includeStarred: false,
        includeImportant: false,
        excludeCategories: [],
        excludeNoReply: false,
        lookbackDays: 30,
        maxThreads: 50,
    };
}
/** Named presets a user can pick in one click. `custom` is not a preset value — see `presetForRules`. */
export const GMAIL_RULES_PRESETS = {
    recommended: defaultGmailAttentionRules(),
    inbox: {
        ...defaultGmailAttentionRules(),
        onlyDirectToMe: false,
        includeStarred: false,
        excludeCategories: ['promotions', 'social'],
        lookbackDays: 7,
    },
    starred: {
        ...defaultGmailAttentionRules(),
        onlyUnread: false,
        onlyDirectToMe: false,
        includeStarred: true,
        includeImportant: true,
        excludeCategories: [],
        excludeNoReply: false,
        lookbackDays: 30,
    },
};
function rulesEqual(a, b) {
    return (a.onlyUnread === b.onlyUnread &&
        a.onlyDirectToMe === b.onlyDirectToMe &&
        a.includeStarred === b.includeStarred &&
        a.includeImportant === b.includeImportant &&
        a.excludeNoReply === b.excludeNoReply &&
        a.lookbackDays === b.lookbackDays &&
        a.maxThreads === b.maxThreads &&
        a.customQuery === b.customQuery &&
        sameStringSet(a.excludeCategories, b.excludeCategories) &&
        sameStringSet(a.labels, b.labels) &&
        sameStringSet(a.senderAllowlist, b.senderAllowlist) &&
        sameStringSet(a.senderBlocklist, b.senderBlocklist));
}
function sameStringSet(a, b) {
    if (a.length !== b.length)
        return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
}
/** Which preset (if any) a rules object exactly matches; `'custom'` otherwise. */
export function presetForRules(rules) {
    for (const [preset, presetRules] of Object.entries(GMAIL_RULES_PRESETS)) {
        if (rulesEqual(rules, presetRules))
            return preset;
    }
    return 'custom';
}
function clamp(value, min, max) {
    if (!Number.isFinite(value))
        return min;
    return Math.min(max, Math.max(min, Math.round(value)));
}
function normalizeStringList(value) {
    if (!Array.isArray(value))
        return [];
    const seen = new Set();
    const out = [];
    for (const raw of value) {
        if (typeof raw !== 'string')
            continue;
        const trimmed = raw.trim().slice(0, MAX_LIST_ENTRY_LENGTH);
        if (!trimmed || seen.has(trimmed))
            continue;
        seen.add(trimmed);
        out.push(trimmed);
        if (out.length >= MAX_LIST_ENTRIES)
            break;
    }
    return out;
}
const GMAIL_CATEGORIES = ['promotions', 'social', 'updates', 'forums'];
function normalizeCategories(value) {
    const list = normalizeStringList(value);
    const out = [];
    for (const entry of list) {
        if (GMAIL_CATEGORIES.includes(entry) &&
            !out.includes(entry)) {
            out.push(entry);
        }
    }
    return out;
}
/** Merges partial/unknown input over the defaults, coercing and clamping every field. Never throws. */
export function normalizeGmailAttentionRules(input) {
    const defaults = defaultGmailAttentionRules();
    const raw = input && typeof input === 'object' ? input : {};
    return {
        onlyUnread: typeof raw.onlyUnread === 'boolean' ? raw.onlyUnread : defaults.onlyUnread,
        onlyDirectToMe: typeof raw.onlyDirectToMe === 'boolean' ? raw.onlyDirectToMe : defaults.onlyDirectToMe,
        includeStarred: typeof raw.includeStarred === 'boolean' ? raw.includeStarred : defaults.includeStarred,
        includeImportant: typeof raw.includeImportant === 'boolean' ? raw.includeImportant : defaults.includeImportant,
        excludeCategories: raw.excludeCategories !== undefined
            ? normalizeCategories(raw.excludeCategories)
            : defaults.excludeCategories,
        excludeNoReply: typeof raw.excludeNoReply === 'boolean' ? raw.excludeNoReply : defaults.excludeNoReply,
        labels: raw.labels !== undefined ? normalizeStringList(raw.labels) : defaults.labels,
        senderAllowlist: raw.senderAllowlist !== undefined
            ? normalizeStringList(raw.senderAllowlist)
            : defaults.senderAllowlist,
        senderBlocklist: raw.senderBlocklist !== undefined
            ? normalizeStringList(raw.senderBlocklist)
            : defaults.senderBlocklist,
        lookbackDays: typeof raw.lookbackDays === 'number'
            ? clamp(raw.lookbackDays, MIN_LOOKBACK_DAYS, MAX_LOOKBACK_DAYS)
            : defaults.lookbackDays,
        maxThreads: typeof raw.maxThreads === 'number'
            ? clamp(raw.maxThreads, MIN_MAX_THREADS, MAX_MAX_THREADS)
            : defaults.maxThreads,
        customQuery: typeof raw.customQuery === 'string'
            ? raw.customQuery.trim().slice(0, MAX_CUSTOM_QUERY_LENGTH)
            : defaults.customQuery,
    };
}
function sanitizeQueryToken(value) {
    return value.trim().replace(/["{}]/g, '').replace(/\s+/g, '-');
}
/**
 * Compiles rules into a Gmail search query. Both the SPA's live preview and
 * the BFF's list fetch call this — the preview never lies about the fetch.
 */
export function buildGmailSearchQuery(rules) {
    const parts = [];
    parts.push(rules.labels.length
        ? `{${rules.labels.map((label) => `label:${sanitizeQueryToken(label)}`).join(' ')}}`
        : 'in:inbox');
    if (rules.onlyUnread)
        parts.push('is:unread');
    const scope = [];
    if (rules.onlyDirectToMe)
        scope.push('to:me');
    if (rules.includeStarred)
        scope.push('is:starred');
    if (rules.includeImportant)
        scope.push('is:important');
    if (scope.length === 1)
        parts.push(scope[0]);
    else if (scope.length > 1)
        parts.push(`{${scope.join(' ')}}`);
    for (const category of rules.excludeCategories)
        parts.push(`-category:${category}`);
    if (rules.excludeNoReply)
        parts.push('-from:noreply -from:no-reply -from:notifications');
    if (rules.senderAllowlist.length) {
        parts.push(`{${rules.senderAllowlist.map((sender) => `from:${sanitizeQueryToken(sender)}`).join(' ')}}`);
    }
    for (const sender of rules.senderBlocklist)
        parts.push(`-from:${sanitizeQueryToken(sender)}`);
    parts.push(`newer_than:${clamp(rules.lookbackDays, MIN_LOOKBACK_DAYS, MAX_LOOKBACK_DAYS)}d`);
    const customQuery = rules.customQuery.trim().slice(0, MAX_CUSTOM_QUERY_LENGTH);
    if (customQuery)
        parts.push(customQuery);
    return parts.join(' ');
}
/**
 * Parses and validates an incoming Pub/Sub push notification payload,
 * decoding the base64 Gmail notification message data.
 */
export function parseGmailPushNotification(payload) {
    if (!payload || typeof payload !== 'object')
        return null;
    const p = payload;
    const data = p.message?.data;
    if (!data || typeof data !== 'string')
        return null;
    try {
        let rawJson;
        if (typeof Buffer !== 'undefined') {
            rawJson = Buffer.from(data, 'base64').toString('utf8');
        }
        else {
            const normalized = data.replace(/-/g, '+').replace(/_/g, '/');
            rawJson = atob(normalized);
        }
        const parsed = JSON.parse(rawJson);
        if (!parsed || typeof parsed !== 'object')
            return null;
        const emailAddress = typeof parsed.emailAddress === 'string' ? parsed.emailAddress.trim().toLowerCase() : '';
        const historyId = parsed.historyId !== undefined && parsed.historyId !== null
            ? String(parsed.historyId).trim()
            : '';
        if (!emailAddress || !historyId)
            return null;
        return { emailAddress, historyId };
    }
    catch {
        return null;
    }
}
/**
 * Strictly parses and validates a Pub/Sub push notification envelope:
 * - Requires non-empty message.messageId (used for deduplication & rejection tracking).
 * - Requires non-empty subscription.
 * - Requires valid base64url data containing emailAddress and historyId.
 */
export function parseValidatedPubSubPushNotification(payload, expectedSubscription) {
    if (!payload || typeof payload !== 'object') {
        return { valid: false, reason: 'Payload must be a non-null object' };
    }
    const p = payload;
    const subscription = typeof p.subscription === 'string' ? p.subscription.trim() : '';
    const message = p.message;
    if (!subscription) {
        return { valid: false, reason: 'Missing subscription in Pub/Sub payload' };
    }
    if (expectedSubscription && subscription !== expectedSubscription.trim()) {
        return {
            valid: false,
            reason: 'Subscription does not match configured topic/subscription',
            subscription,
        };
    }
    if (!message || typeof message !== 'object') {
        return { valid: false, reason: 'Missing message in Pub/Sub payload', subscription };
    }
    const deliveryId = typeof message.messageId === 'string' ? message.messageId.trim() : '';
    if (!deliveryId) {
        return { valid: false, reason: 'Missing message.messageId in Pub/Sub payload', subscription };
    }
    const data = parseGmailPushNotification(payload);
    if (!data) {
        return {
            valid: false,
            reason: 'Malformed or missing base64 Gmail notification data',
            deliveryId,
            subscription,
        };
    }
    return {
        valid: true,
        notification: {
            deliveryId,
            subscription,
            publishTime: message.publishTime,
            data,
        },
    };
}
/** Default threshold before watch expiration to renew: 2 days (48 hours). */
export const DEFAULT_WATCH_RENEWAL_THRESHOLD_MS = 2 * 24 * 60 * 60 * 1000;
/**
 * Determines whether a Gmail watch needs renewal based on its expiration timestamp.
 * Returns true if expiration is missing, invalid, already expired, or expiring within thresholdMs.
 */
export function isGmailWatchExpiringSoon(expiration, now = Date.now(), thresholdMs = DEFAULT_WATCH_RENEWAL_THRESHOLD_MS) {
    if (!expiration)
        return true;
    const exp = typeof expiration === 'number' ? expiration : Number(expiration);
    if (!Number.isFinite(exp) || exp <= 0)
        return true;
    return exp - now < thresholdMs;
}
//# sourceMappingURL=gmail.js.map